import { z } from 'zod';

export const methodSchema = z.enum([
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  '*',
]);

export const failureModeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('httpError'),
    status: z.number().int().min(100).max(599),
    body: z.unknown().optional(),
  }),
  z.object({
    kind: z.literal('latency'),
    delayMs: z.number().int().min(0).max(600_000),
  }),
  z.object({ kind: z.literal('networkDrop') }),
  z.object({ kind: z.literal('invalidJson') }),
  z.object({
    kind: z.literal('wrongShape'),
    // Optional override; the middleware defaults to 200 when omitted, which
    // covers both the defensive-coding use case and a 200-OK stub response.
    status: z.number().int().min(100).max(599).optional(),
    // Required (null is allowed) — an absent payload would send an empty body.
    payload: z.unknown().refine(value => value !== undefined, {
      message: 'wrongShape mode requires a payload',
    }),
  }),
]);

/**
 * Prefix for client-generated temporary ids. New rules, imports, and presets
 * get one as a stable React key while editing; the server replaces any temp or
 * empty id with a real UUID on save. Shared so client and server agree on it.
 */
export const CLIENT_TEMP_ID_PREFIX = 'c-';

/**
 * The malformed JSON the invalidJson mode always returns. Shared so client
 * and server agree on the exact string returned and previewed.
 */
export const INVALID_JSON_PAYLOAD = '{"data": [{,]}';

export const ruleSchema = z.object({
  // Empty on a brand-new rule, a `CLIENT_TEMP_ID_PREFIX` temp id while it's
  // only client-side, or a server-assigned UUID once the ruleset is saved.
  id: z.string(),
  name: z.string().min(1).max(200),
  pathPattern: z.string().min(1).max(500),
  method: methodSchema,
  mode: failureModeSchema,
  probability: z.number().min(0).max(1),
  enabled: z.boolean(),
});

export const rulesArraySchema = z.array(ruleSchema);

// Wire format for the import/export file. The version field is reserved for
// forward compatibility — bump it if the rule shape ever changes.
export const rulesPayloadSchema = z.object({
  version: z.literal(1),
  rules: rulesArraySchema,
});

export const stateSchema = z.object({ enabled: z.boolean() });

export type Method = z.infer<typeof methodSchema>;
export type FailureMode = z.infer<typeof failureModeSchema>;
export type FailureModeKind = FailureMode['kind'];
export type Rule = z.infer<typeof ruleSchema>;
export type RulesPayload = z.infer<typeof rulesPayloadSchema>;
